import { IonIcon } from '@ionic/react';
import {
  people,
  flag,
  grid,
  cube,
  location,
  shieldCheckmark,
  lockClosed,
  documentText,
  cog,
  shareSocial,
  key,
  shield,
  time,
  idCard,
  checkmarkCircle,
  ban,
  alertCircle,
  scale,
  mail,
  helpBuoy,
  chatbubble,
  warning,
  search,
  person,
  briefcase,
  helpCircle,
  arrowForward,
  globe,
  analytics,
  trendingUp,
} from 'ionicons/icons';
import { StaticPage } from '@/components/StaticPage';

/* Reusable contact channel row for the Contact page */
function ContactRow({ icon, label, value, href }: { icon: string; label: string; value: string; href: string }) {
  return (
    <a className="bp-static-contact-row" href={href} style={{ textDecoration: 'none' }}>
      <span className="bp-static-contact-icon">
        <IonIcon icon={icon} style={{ fontSize: '1.2rem', color: 'var(--bp-primary-contrast)' }} />
      </span>
      <div>
        <div className="label">{label}</div>
        <div className="value">{value}</div>
      </div>
    </a>
  );
}

export function AboutPage() {
  return (
    <StaticPage
      title="About BluePages"
      heroIcon={people}
      updated="July 2026"
      intro="BluePages is South Africa's contract & project marketplace — built to connect skilled tradespeople, professionals and contractors with the homeowners and businesses that need their work done."
      sections={[
        {
          heading: 'Our mission',
          icon: flag,
          accent: 'primary',
          body: (
            <p>
              Make it effortless for South Africans to find contract and project work, and for employers
              to find trusted local talent. No recruiters, no placement fees, no gatekeeping — just skilled
              people and the work that needs them.
            </p>
          ),
        },
        {
          heading: 'What we do',
          icon: grid,
          accent: 'info',
          body: (
            <ul className="bp-dot-list">
              <li>
                <strong>For pros & contractors</strong> — build a profile once, get matched by trade,
                location and availability, then apply to contract jobs across the country with a single tap.
              </li>
              <li>
                <strong>For employers & homeowners</strong> — post contract and project work, browse matched
                candidates, and connect directly with verified pros.
              </li>
              <li>
                <strong>AI matching</strong> — our matching surfaces roles and candidates that align with
                your trade, location and goals, so you spend less time searching.
              </li>
            </ul>
          ),
        },
        {
          heading: 'Part of the Rosta platform',
          icon: cube,
          accent: 'secondary',
          body: (
            <p>
              BluePages is a Rosta platform. Rosta is a South African HR management & skills-development
              platform, so the marketplace fits naturally into the wider world of compliant, skills-first
              hiring.
            </p>
          ),
        },
        {
          heading: 'Built South Africa-first',
          icon: location,
          accent: 'success',
          body: (
            <p>
              BluePages is built for the South African job market — trades, provinces and the way people
              actually search for work here. You'll find real project work, not ghost jobs, and anyone can
              choose to get vetted for an extra layer of assurance.
            </p>
          ),
        },
        {
          heading: 'Verified, when it matters most',
          icon: idCard,
          accent: 'info',
          body: (
            <p>
              Trust is the currency of great work. That's why we let pros and employers step up and get
              <strong> vetted</strong> — a considered verification that signals real credentials, real
              accountability and a serious intent to deliver. Vetted profiles wear a mark of distinction,
              rise to the top, and give the other side the confidence to say yes, faster. Vetting is an
              optional, paid badge of assurance; pricing is being finalised as we grow, and we'll share it
              well before it goes live.
            </p>
          ),
        },
        {
          heading: 'Our promise to you',
          icon: shieldCheckmark,
          accent: 'warning',
          body: (
            <ul className="bp-dot-list">
              <li>Optional vetting for pros and employers who want to stand out.</li>
              <li>Transparent, fee-free connecting between candidates and clients.</li>
              <li>Your data handled lawfully under POPIA.</li>
            </ul>
          ),
        },
      ]}
      cta={{
        title: 'Ready to find your next opportunity?',
        body: 'Create a free profile or post your first project in minutes.',
        buttonLabel: 'Explore BluePages',
        buttonIcon: arrowForward,
        routerLink: '/home',
      }}
    />
  );
}

export function PrivacyPage() {
  return (
    <StaticPage
      title="Privacy"
      heroIcon={lockClosed}
      updated="July 2026"
      intro="We are committed to protecting your personal information and handling it lawfully, in line with the Protection of Personal Information Act (POPIA)."
      sections={[
        {
          heading: 'Information we collect',
          icon: documentText,
          accent: 'primary',
          body: (
            <ul className="bp-dot-list">
              <li>Profile details you provide — name, trade, skills, location, availability and bio.</li>
              <li>Account and sign-in information used to authenticate you.</li>
              <li>
                Activity such as the roles you view and apply to, so we can improve matching and your
                experience.
              </li>
            </ul>
          ),
        },
        {
          heading: 'How we use your information',
          icon: cog,
          accent: 'info',
          body: (
            <p>
              To operate the marketplace, match candidates with contract work, verify accounts, keep the
              platform safe, and communicate with you about your account and relevant opportunities. We do
              not use your data for unrelated advertising.
            </p>
          ),
        },
        {
          heading: 'Who we share with',
          icon: shareSocial,
          accent: 'warning',
          body: (
            <p>
              We share your profile information with employers or homeowners only when you apply to, or
              express interest in, their work. We do not sell your personal information. We may disclose
              information where required by South African law.
            </p>
          ),
        },
        {
          heading: 'Your rights under POPIA',
          icon: key,
          accent: 'success',
          body: (
            <ul className="bp-dot-list">
              <li>Request access to the personal information we hold about you.</li>
              <li>Ask us to correct or update inaccurate information.</li>
              <li>Object to certain processing, or request deletion where the law allows.</li>
              <li>Lodge a complaint with our Information Officer or the Information Regulator.</li>
            </ul>
          ),
        },
        {
          heading: 'How we protect your data',
          icon: shield,
          accent: 'secondary',
          body: (
            <p>
              We apply appropriate technical and organisational measures to protect your data in transit and
              at rest, and limit access to staff who need it to serve you.
            </p>
          ),
        },
        {
          heading: 'How long we keep it',
          icon: time,
          accent: 'danger',
          body: (
            <p>
              We keep personal information only as long as needed for the purposes above or as required by
              law, then delete or de-identify it.
            </p>
          ),
        },
      ]}
      cta={{
        title: 'Questions about your data?',
        body: 'Our Information Officer is happy to help with access, correction or deletion requests.',
        buttonLabel: 'Contact our Information Officer',
        buttonIcon: mail,
        routerLink: '/contact',
      }}
    />
  );
}

export function TermsPage() {
  return (
    <StaticPage
      title="Terms of Use"
      heroIcon={documentText}
      updated="July 2026"
      intro="By using BluePages you agree to these terms. If you do not agree, please do not use the platform."
      sections={[
        {
          heading: 'Who can use BluePages',
          icon: idCard,
          accent: 'primary',
          body: (
            <p>
              You must provide accurate information, keep your account secure, and be at least 18 years old
              (or have parental consent) to use BluePages.
            </p>
          ),
        },
        {
          heading: 'Using BluePages responsibly',
          icon: checkmarkCircle,
          accent: 'success',
          body: (
            <ul className="bp-dot-list">
              <li>Post truthful profiles, jobs and content.</li>
              <li>Treat other users respectfully and communicate in good faith.</li>
              <li>Keep your qualifications and certifications accurate and up to date.</li>
            </ul>
          ),
        },
        {
          heading: 'What is not allowed',
          icon: ban,
          accent: 'danger',
          body: (
            <ul className="bp-dot-list">
              <li>No fraud, harassment, discrimination or unlawful content.</li>
              <li>No misrepresenting qualifications, certifications or identity.</li>
              <li>No scraping, spamming or attempts to circumvent the platform.</li>
            </ul>
          ),
        },
        {
          heading: 'No employment relationship',
          icon: people,
          accent: 'warning',
          body: (
            <p>
              BluePages is a marketplace. We are not a party to any contract between candidates and
              employers, and are not an employment agency or recruiter.
            </p>
          ),
        },
        {
          heading: 'Accounts, suspension & termination',
          icon: alertCircle,
          accent: 'info',
          body: (
            <p>
              We may remove content or suspend accounts that breach these terms. You can delete your account
              at any time from your profile settings.
            </p>
          ),
        },
        {
          heading: 'Governing law',
          icon: scale,
          accent: 'secondary',
          body: <p>These terms are governed by the laws of South Africa.</p>,
        },
      ]}
      cta={{
        title: 'Read how we handle your data',
        body: 'Our Privacy Policy explains what we collect and your rights under POPIA.',
        buttonLabel: 'View Privacy Policy',
        buttonIcon: documentText,
        routerLink: '/privacy',
      }}
    />
  );
}

export function ContactPage() {
  return (
    <StaticPage
      title="Contact"
      heroIcon={mail}
      updated="July 2026"
      intro="We'd love to hear from you — whether it's a question, feedback, or a problem to report. Pick the right channel and we'll get back to you."
      metaDescription="Contact BluePages — South Africa's skills-first job marketplace. Reach support, general enquiries, safety and our POPIA Information Officer by email."
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'BluePages',
        url: 'https://bluepages.co.za',
        email: 'hello@bluepages.co.za',
        contactPoint: [
          { '@type': 'ContactPoint', contactType: 'customer support', email: 'support@bluepages.co.za' },
          { '@type': 'ContactPoint', contactType: 'sales', email: 'hello@bluepages.co.za' },
          { '@type': 'ContactPoint', contactType: 'safety', email: 'safety@bluepages.co.za' },
          {
            '@type': 'ContactPoint',
            contactType: 'data protection officer',
            email: 'privacy@bluepages.co.za',
            contactOption: 'https://www.justice.gov.za/inforeg/',
          },
        ],
      }}
      sections={[
        {
          heading: 'Get in touch',
          icon: chatbubble,
          accent: 'primary',
          body: (
            <>
              <ContactRow icon={helpBuoy} label="Support" value="support@bluepages.co.za" href="mailto:support@bluepages.co.za" />
              <ContactRow icon={globe} label="General enquiries" value="hello@bluepages.co.za" href="mailto:hello@bluepages.co.za" />
              <ContactRow icon={warning} label="Report a problem or scam" value="safety@bluepages.co.za" href="mailto:safety@bluepages.co.za" />
              <ContactRow icon={shield} label="Information Officer (POPIA)" value="privacy@bluepages.co.za" href="mailto:privacy@bluepages.co.za" />
            </>
          ),
        },
        {
          heading: 'Office & hours',
          icon: time,
          accent: 'warning',
          body: (
            <p>
              BluePages is a Rosta platform. Our support team is available Monday to Friday, 08:00–17:00
              SAST. We aim to reply to every message within one business day.
            </p>
          ),
        },
      ]}
    />
  );
}

export function HelpPage() {
  return (
    <StaticPage
      title="Help"
      heroIcon={helpCircle}
      updated="July 2026"
      intro="Quick answers to get the most out of BluePages."
      sections={[
        {
          heading: 'Finding work',
          icon: search,
          accent: 'primary',
          body: (
            <p>
              Use the search on the home page and start typing your trade — we suggest recognised occupation
              roles as you type. Pick the right one, then filter by location and availability.
            </p>
          ),
        },
        {
          heading: 'Building a standout profile',
          icon: person,
          accent: 'success',
          body: (
            <ul className="bp-dot-list">
              <li>Add your skills, availability, preferred areas and expected pay.</li>
              <li>Upload certifications where relevant to build trust.</li>
              <li>A complete profile gets matched more often and stands out to employers.</li>
            </ul>
          ),
        },
        {
          heading: 'For employers posting jobs',
          icon: briefcase,
          accent: 'info',
          body: (
            <p>
              From your employer dashboard, post a contract or project, describe the work, and browse matched
              candidates. Connect directly — no fees.
            </p>
          ),
        },
        {
          heading: 'Verifying your email',
          icon: mail,
          accent: 'warning',
          body: (
            <p>
              Did not receive your verification email? Open the sign-in page and use “Verify your email” to
              resend it. Check your spam folder too.
            </p>
          ),
        },
        {
          heading: 'Resetting your password',
          icon: key,
          accent: 'secondary',
          body: (
            <p>
              On the sign-in page, choose “Forgot password”. We will send a reset link to your email — it
              expires after a short time for your security.
            </p>
          ),
        },
        {
          heading: 'Staying safe on BluePages',
          icon: shield,
          accent: 'danger',
          body: (
            <ul className="bp-dot-list">
              <li>Keep all communication and payments on-platform until you are hired.</li>
              <li>Never share banking OTPs or ID numbers with another user.</li>
              <li>Report anything suspicious to safety@bluepages.co.za.</li>
            </ul>
          ),
        },
      ]}
      cta={{
        title: 'Still need a hand?',
        body: 'Our support team is one email away.',
        buttonLabel: 'Contact support',
        buttonIcon: mail,
        routerLink: '/contact',
      }}
    />
  );
}

export function AIMatchingPage() {
  return (
    <StaticPage
      title="How AI Matching Works"
      heroIcon={analytics}
      updated="August 2026"
      intro="BluePages uses artificial intelligence to connect the right candidates with the right opportunities — faster, fairer, and with less effort on your part."
      metaDescription="Learn how BluePages uses AI and machine learning to match skilled professionals with job opportunities across South Africa."
      sections={[
        {
          heading: 'How BluePages finds the right match',
          icon: search,
          accent: 'primary',
          body: (
            <p>
              When a candidate creates a profile or an employer posts a job, BluePages goes beyond simple keyword
              matching. Our system analyses trade skills, qualifications, location, availability, salary expectations
              and work preferences to surface the most relevant opportunities — for both sides. The result is a
              shorter search, fewer irrelevant results, and better outcomes for everyone.
            </p>
          ),
        },
        {
          heading: 'Skills & trade alignment',
          icon: grid,
          accent: 'info',
          body: (
            <ul className="bp-dot-list">
              <li>
                Every candidate profile maps to official OFO occupation codes and a curated list of trade skills,
                so the system understands what you do — even if two people describe the same job differently.
              </li>
              <li>
                Seniority level matters. An apprentice electrician and a master electrician are matched to
                different roles, with salary expectations adjusted accordingly.
              </li>
              <li>
                Skills that appear on your profile and work history carry more weight than skills you simply list,
                encouraging honest and complete profiles.
              </li>
            </ul>
          ),
        },
        {
          heading: 'Location & availability',
          icon: location,
          accent: 'success',
          body: (
            <ul className="bp-dot-list">
              <li>
                BluePages matches by province, city and commute radius — not just a pin on a map. Candidates
                indicate how far they are willing to travel, and jobs specify the work location.
              </li>
              <li>
                Availability windows (Immediate, Two Weeks, One Month) help employers find someone who can start
                when they need them, reducing wasted conversations.
              </li>
              <li>
                Work type preferences — Permanent, Contract, Part-Time or Freelance — are factored in so you
                only see opportunities that fit your working style.
              </li>
            </ul>
          ),
        },
        {
          heading: 'What employers see',
          icon: briefcase,
          accent: 'warning',
          body: (
            <p>
              When an employer posts a job, the system ranks candidate profiles by how closely they match the
              requirements — trade skills, location, salary range and availability. Employers see a relevance
              score alongside each profile, not just a list sorted by date. This means the most suitable
              candidates surface first, whether they signed up yesterday or six months ago.
            </p>
          ),
        },
        {
          heading: 'Getting smarter over time',
          icon: trendingUp,
          accent: 'secondary',
          body: (
            <ul className="bp-dot-list">
              <li>
                The system learns from outcomes. When candidates are shortlisted, contacted or hired for specific
                types of roles, those signals improve future matches for everyone.
              </li>
              <li>
                Profile completeness affects visibility. Candidates with detailed bios, verified identities and
                complete work histories rank higher — rewarding effort and transparency.
              </li>
              <li>
                Matching quality is reviewed regularly. We monitor whether matched candidates actually apply and
                whether employers respond, and adjust the ranking model accordingly.
              </li>
            </ul>
          ),
        },
        {
          heading: 'Your data, your control',
          icon: shield,
          accent: 'danger',
          body: (
            <ul className="bp-dot-list">
              <li>
                You decide what is visible. Salary expectations, contact details and location precision are all
                controlled by you — public, private or premium tiers.
              </li>
              <li>
                BluePages is POPIA compliant. Your personal information is never shared with third parties or
                used for purposes other than matching you with relevant work.
              </li>
              <li>
                You can opt out of AI matching at any time. Your profile remains on the platform, but the system
                will stop suggesting you for roles — and you can turn it back on whenever you like.
              </li>
            </ul>
          ),
        },
      ]}
      cta={{
        title: 'Ready to get matched?',
        body: 'Create your profile and let BluePages find the right opportunities for you.',
        buttonLabel: 'Create your profile',
        buttonIcon: arrowForward,
        routerLink: '/register',
      }}
    />
  );
}
